import {
  getSelectedCompaniesStorageScope,
  parseRawSelectedCompaniesValue,
  parseStoredSelectedCompanies,
  serializeSelectedCompanies,
  type SelectedCompany,
} from './selected-companies-payload'
import {getSharedCookieDomain} from './shared-cookie-domain'

export type {SelectedCompany}

const LEGACY_LOCAL_STORAGE_KEY = 'app:companies:selected'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

function getSelectedCompaniesCookieKey() {
  return process.env.REACT_APP_SELECTED_COMPANIES_KEY ?? '@Brain-selected-companies'
}

function getScopedLocalStorageKey() {
  return `${LEGACY_LOCAL_STORAGE_KEY}:v2:${getSelectedCompaniesStorageScope()}`
}

function readCookieValue(): string | null {
  const key = getSelectedCompaniesCookieKey()
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedKey}=([^;]*)`))

  return match?.[1] ?? null
}

function clearLegacyLocalStorage() {
  window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY)
  window.localStorage.removeItem(`${LEGACY_LOCAL_STORAGE_KEY}:v2`)
}

function readLegacyLocalStorage(): SelectedCompany[] {
  const scopedRaw = window.localStorage.getItem(getScopedLocalStorageKey())
  if (scopedRaw == null) return []

  try {
    const value = JSON.parse(scopedRaw)
    const companies = parseStoredSelectedCompanies(value)
    if (companies.length) return companies

    if (companies.length === 0 && value != null) {
      window.localStorage.removeItem(getScopedLocalStorageKey())
    }
  } catch {
    window.localStorage.removeItem(getScopedLocalStorageKey())
  }

  return []
}

function clearSelectedCompaniesCookie() {
  const key = getSelectedCompaniesCookieKey()
  const secure = window.location.protocol === 'https:'
  const domain = getSharedCookieDomain()

  let cookie = `${key}=; path=/; max-age=0; samesite=lax`

  if (secure) cookie += '; secure'
  if (domain) cookie += `; domain=${domain}`

  document.cookie = cookie
}

export function readSelectedCompanies(): SelectedCompany[] {
  const rawCookie = readCookieValue()

  if (rawCookie) {
    const {companies, shouldClear} = parseRawSelectedCompaniesValue(rawCookie)
    if (companies.length) return companies
    if (shouldClear) clearSelectedCompaniesCookie()
  }

  return readLegacyLocalStorage()
}

export function writeSelectedCompanies(companies: SelectedCompany[]) {
  const key = getSelectedCompaniesCookieKey()
  const secure = window.location.protocol === 'https:'
  const domain = getSharedCookieDomain()
  const serialized = serializeSelectedCompanies(companies)

  clearLegacyLocalStorage()

  if (companies.length) {
    window.localStorage.setItem(getScopedLocalStorageKey(), serialized)
  } else {
    window.localStorage.removeItem(getScopedLocalStorageKey())
  }

  if (!companies.length) {
    clearSelectedCompaniesCookie()
    return
  }

  const encodedValue = encodeURIComponent(serialized)
  let cookie = `${key}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`

  if (secure) cookie += '; secure'
  if (domain) cookie += `; domain=${domain}`

  document.cookie = cookie
}

export function clearSelectedCompaniesFromClientStorage() {
  writeSelectedCompanies([])
}
